import React, { useEffect } from 'react';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import CartDrawer from '../components/cart/CartDrawer';
import { siteImages, CONTACT_INFO } from '../data/mock';
import { Link } from 'react-router-dom';
import { Shield, CheckCircle, Clock, Package, RefreshCw, ArrowRight, Mail, Phone, Award, Eye } from 'lucide-react';

const GuaranteePage = () => {
  useEffect(() => {
    document.title = 'Our Guarantee | Clarity Lens Co';
  }, []);

  const guarantees = [
    {
      icon: Clock,
      title: '30-Day Money Back',
      description: "Not completely satisfied? Return your purchase within 30 days for a full refund. No questions asked, no restocking fees."
    },
    {
      icon: Shield,
      title: '1-Year Warranty',
      description: 'All eyewear is covered by our comprehensive 1-year warranty against manufacturing defects including frames, hinges, and lens coatings.'
    },
    {
      icon: Package,
      title: 'Free Replacement',
      description: "If your item arrives damaged or defective, we'll send a replacement immediately at no additional cost to you."
    },
    {
      icon: RefreshCw,
      title: 'Easy Returns',
      description: 'Simple, hassle-free return process. Just contact us, and we\'ll provide instructions and a prepaid shipping label.'
    }
  ];

  const covered = [
    'Manufacturing defects',
    'Frame breakage under normal use',
    'Hinge failures',
    'Lens coating issues',
    'Color fading or peeling',
    'Nose pad deterioration'
  ];

  const notCovered = [
    'Accidental damage or drops',
    'Scratches from improper cleaning',
    'Loss or theft',
    'Damage from extreme heat',
    'Normal wear after warranty period',
    'Unauthorized modifications'
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <CartDrawer />
      
      <main className="pt-[104px]">
        {/* Hero Banner */}
        <section className="relative h-[240px] overflow-hidden bg-neutral-100">
          <img 
            src={siteImages.ctaBackground}
            alt="Our Guarantee"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-neutral-900/80 via-neutral-900/50 to-transparent" />
          <div className="absolute inset-0 flex items-center">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
              <div className="max-w-lg">
                <span className="inline-flex items-center gap-2 px-3 py-1.5 bg-white/10 backdrop-blur-sm rounded-full text-white text-xs font-medium mb-4">
                  <Award className="w-3.5 h-3.5" />
                  Our Promise
                </span>
                <h1 className="font-display text-3xl sm:text-4xl text-white font-semibold tracking-tight mb-2">
                  Our Guarantee
                </h1>
                <p className="text-white/80">Your satisfaction is 100% guaranteed</p>
              </div>
            </div>
          </div>
        </section>

        {/* Main Guarantee Statement */}
        <section className="py-16 bg-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <div className="w-20 h-20 bg-neutral-900 rounded-2xl flex items-center justify-center mx-auto mb-6">
              <Shield className="w-10 h-10 text-white" />
            </div>
            <h2 className="font-display text-2xl text-neutral-900 font-semibold mb-4">
              The Clarity Lens Co Promise
            </h2>
            <p className="text-neutral-600 text-lg leading-relaxed max-w-2xl mx-auto">
              We stand behind every product we sell. If you're not completely satisfied with your purchase, we'll make it right. It's that simple.
            </p>
          </div>
        </section>

        {/* Guarantee Cards */}
        <section className="py-16 bg-neutral-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid md:grid-cols-2 gap-6">
              {guarantees.map((item, i) => (
                <div key={i} className="bg-white rounded-2xl p-8 border border-neutral-200">
                  <div className="w-14 h-14 bg-neutral-100 rounded-xl flex items-center justify-center mb-5">
                    <item.icon className="w-7 h-7 text-neutral-700" />
                  </div>
                  <h3 className="font-display text-xl text-neutral-900 font-semibold mb-3">{item.title}</h3>
                  <p className="text-neutral-600 leading-relaxed">{item.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Warranty Coverage */}
        <section className="py-16 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="font-display text-2xl text-neutral-900 font-semibold mb-2">
                Warranty Coverage Details
              </h2>
              <p className="text-neutral-600">What's included in your 1-year warranty</p>
            </div>
            
            <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
              <div className="bg-green-50 border border-green-100 rounded-xl p-6">
                <h3 className="font-semibold text-neutral-900 mb-4 flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-green-600" />
                  What's Covered
                </h3>
                <ul className="space-y-3">
                  {covered.map((item, i) => (
                    <li key={i} className="flex items-start gap-2 text-neutral-600">
                      <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
              <div className="bg-neutral-50 border border-neutral-200 rounded-xl p-6">
                <h3 className="font-semibold text-neutral-900 mb-4 flex items-center gap-2">
                  <Shield className="w-5 h-5 text-neutral-500" />
                  What's Not Covered
                </h3>
                <ul className="space-y-3">
                  {notCovered.map((item, i) => (
                    <li key={i} className="flex items-start gap-2 text-neutral-600">
                      <span className="w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <span className="w-1.5 h-1.5 bg-neutral-400 rounded-full" />
                      </span>
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* How to Claim */}
        <section className="py-16 bg-neutral-900">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="font-display text-2xl text-white font-semibold mb-2">
                How to Make a Claim
              </h2>
              <p className="text-neutral-400">Simple process, quick resolution</p>
            </div>
            
            <div className="grid sm:grid-cols-3 gap-6">
              {[
                { step: '1', title: 'Contact Us', desc: 'Email or call with your order number and issue description' },
                { step: '2', title: 'Get Approved', desc: 'We\'ll review and approve your claim within 24-48 hours' },
                { step: '3', title: 'Resolution', desc: 'Receive your replacement or refund quickly' }
              ].map((item, i) => (
                <div key={i} className="text-center">
                  <div className="w-12 h-12 bg-white rounded-xl flex items-center justify-center mx-auto mb-4">
                    <span className="text-xl font-bold text-neutral-900">{item.step}</span>
                  </div>
                  <h4 className="font-medium text-white mb-2">{item.title}</h4>
                  <p className="text-neutral-400 text-sm">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Contact for Claims */}
        <section className="py-16 bg-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="bg-neutral-50 rounded-2xl p-8 text-center">
              <h3 className="font-semibold text-neutral-900 text-xl mb-2">Need to Make a Claim?</h3>
              <p className="text-neutral-600 mb-6">Our customer support team is ready to help</p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                <a 
                  href={`mailto:${CONTACT_INFO.email}`}
                  className="flex items-center gap-2 px-6 py-3 bg-neutral-900 hover:bg-neutral-800 text-white font-medium rounded-full transition-colors"
                >
                  <Mail className="w-4 h-4" />
                  {CONTACT_INFO.email}
                </a>
                <a 
                  href={`tel:${CONTACT_INFO.phone.replace(/[^0-9]/g, '')}`}
                  className="flex items-center gap-2 px-6 py-3 bg-white border border-neutral-200 hover:border-neutral-300 text-neutral-700 font-medium rounded-full transition-colors"
                >
                  <Phone className="w-4 h-4" />
                  {CONTACT_INFO.phone}
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-16 bg-neutral-50">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="font-display text-2xl text-neutral-900 font-semibold mb-4">
              Shop with Confidence
            </h2>
            <p className="text-neutral-600 mb-8">
              Every purchase is protected by our comprehensive guarantee
            </p>
            <Link 
              to="/shop"
              className="inline-flex items-center gap-2 px-8 py-4 bg-neutral-900 hover:bg-neutral-800 text-white font-medium rounded-full transition-colors"
            >
              Browse Collection
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </section>
      </main>
      
      <Footer />
    </div>
  );
};

export default GuaranteePage;
